import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  InstallStatus,
  InstallDbTestReq,
  InstallRedisTestReq,
  InstallWebApiTestReq,
  ConnTestRes,
  AdminSetupReq,
  InstallFinalizeReq,
  ActionOk,
} from "@etheria/shared";
import { api } from "../lib/api";
import { qk } from "./keys";

/**
 * Domaine INSTALLEUR — SITE_SPEC §8 (wizard /install façon Azuriom).
 * status → db/test → redis/test → create-admin → finalize (écrit le marqueur, verrouille).
 *
 * `useInstallStatus` est aussi lu par l'installer-guard (App.tsx) pour rediriger vers /install
 * tant que `installed === false`.
 */

/** GET /api/install/status — { installed, step? }. */
export function useInstallStatus() {
  return useQuery({
    queryKey: qk.install.status,
    queryFn: ({ signal }) => api.get<InstallStatus>("/install/status", { signal }),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** POST /api/install/db/test — teste la connexion MySQL/MariaDB. */
export function useDbTest() {
  return useMutation({
    mutationFn: (body: InstallDbTestReq) => api.post<ConnTestRes>("/install/db/test", body),
  });
}

/** POST /api/install/redis/test — PING Redis. */
export function useRedisTest() {
  return useMutation({
    mutationFn: (body: InstallRedisTestReq) => api.post<ConnTestRes>("/install/redis/test", body),
  });
}

/** POST /api/install/webapi/test — ping du pont serveur Minecraft (GET /api/health, X-Etheria-Api-Key). */
export function useWebApiTest() {
  return useMutation({
    mutationFn: (body: InstallWebApiTestReq) => api.post<ConnTestRes>("/install/webapi/test", body),
  });
}

/** POST /api/install/create-admin — crée le super-admin du site (validation, non finalisant). */
export function useCreateAdmin() {
  return useMutation({
    mutationFn: (body: AdminSetupReq) => api.post<ActionOk>("/install/create-admin", body),
  });
}

/** POST /api/install/finalize — persiste + écrit le marqueur d'installation (verrou). */
export function useFinalizeInstall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InstallFinalizeReq) => api.post<InstallStatus>("/install/finalize", body),
    onSuccess: (res) => {
      qc.setQueryData<InstallStatus>(qk.install.status, res);
      void qc.invalidateQueries({ queryKey: qk.install.status });
    },
  });
}
